/**
 * Background Control Group Component
 *
 * A comprehensive background control group that provides color, image, gradient,
 * pattern and overlay controls with optional reset functionality.
 *
 * @package RedfoxThankYou
 * @since 1.0.0
 */

import './style.scss';

import { PanelBody, ResetButton } from '@redfox-thank-you/components';
import { BaseControl, ColorPicker } from '@redfox-thank-you/controls';
import { useSelect } from '@wordpress/data';
import { store as editorStore } from '@wordpress/editor';
import { __ } from '@wordpress/i18n';
import React from 'react';

import { deviceTypeToBreakpoint,getResponsiveValues, updateResponsiveProperty } from '../common';
import { DEFAULT_ENABLED_CONTROLS } from './constants';
import { BackgroundPremiumControls } from './premium';
import {
	BackgroundControlProps,
	BackgroundValues,
} from './types';
import { hasAnyBackgroundValues, resetBackgroundAttributes } from './utils';

export const BackgroundControl: React.FC< BackgroundControlProps > = ( {
	attributes,
	setAttributes,
	enabledControls = DEFAULT_ENABLED_CONTROLS,
	title = __( 'Background Settings', 'redfox-thank-you' ),
	className = '',
	allowReset = true,
	resetOptions = {},
	initialOpen = false,
	responsive = true,
	showIndividualResets = true,
	showGroupReset = true,
} ) => {
	// Merge default enabled controls with provided ones
	const controls = { ...DEFAULT_ENABLED_CONTROLS, ...enabledControls };

	// Get current device type for responsive controls
	const deviceType = useSelect( ( select ) => {
		const editorSelect = select(
			editorStore,
		) as unknown as import('@wordpress/editor').EditorStore;
		return editorSelect?.getDeviceType?.() ?? 'Desktop';
	}, [] );

	const activeBreakpoint = deviceTypeToBreakpoint( deviceType );

	const useResponsive = responsive && controls.responsive;

	// Get current values for the active breakpoint
	const currentValues =
		getResponsiveValues( attributes, activeBreakpoint ) || {};

	/**
	 * Handle attribute updates for different control types
	 */
	const updateAttribute =
		( key: keyof BackgroundValues ) =>
			( value: string | number | undefined ) => {
				const updateData = updateResponsiveProperty(
					attributes,
					key,
					value,
					activeBreakpoint,
				);
				setAttributes( updateData );
			};

	/**
	 * Reset all background attributes
	 */
	const resetAllBackground = () => {
		const resetValues = { ...resetBackgroundAttributes(), ...resetOptions };
		setAttributes( resetValues );
	};

	/**
	 * Reset individual background property
	 */
	const resetIndividualProperty =
		( property: keyof BackgroundValues ) => () => {
			const updateData = updateResponsiveProperty(
				attributes,
				property,
				undefined,
				activeBreakpoint,
			);
			setAttributes( updateData );
		};

	/**
	 * Check if individual property has a value
	 */
	const hasPropertyValue = ( property: keyof BackgroundValues ): boolean => {
		const value = currentValues[ property ];
		if ( property === 'backgroundImage' ) {
			return !! ( value as BackgroundValues[ 'backgroundImage' ] )?.url;
		}
		if ( property === 'backgroundOverlay' ) {
			return !! ( value as BackgroundValues[ 'backgroundOverlay' ] )
				?.enabled;
		}
		return !! value;
	};

	/**
	 * Check if any background attributes are set
	 */
	const hasBackgroundValues = () => {
		return hasAnyBackgroundValues( attributes );
	};

	/**
	 * Render header actions (reset button)
	 */
	const renderHeaderActions = () => {
		return (
			<ResetButton
				onReset={ resetAllBackground }
				show={ allowReset && showGroupReset && hasBackgroundValues() }
				label={ __( 'Reset Background', 'redfox-thank-you' ) }
				className="redfox-thank-you-background-control__reset"
			/>
		);
	};

	return (
		<PanelBody
			title={ title }
			initialOpen={ initialOpen }
			className={ `redfox-thank-you-background-control ${ className }` }
			showResponsive={ useResponsive }
			responsiveProps={ {
				showLabels: false,
			} }
			headerActions={ renderHeaderActions() }
		>
			{ /* Background Color */ }
			{ controls.backgroundColor && (
				<BaseControl
					id="background-color"
					label={ __( 'Background Color', 'redfox-thank-you' ) }
					help={ __(
						'Choose a background color.',
						'redfox-thank-you',
					) }
					inline
					showResetButton={ showIndividualResets }
					onReset={ resetIndividualProperty( 'backgroundColor' ) }
					hasValue={ hasPropertyValue( 'backgroundColor' ) }
				>
					<ColorPicker
						value={ currentValues.backgroundColor }
						onChange={ updateAttribute( 'backgroundColor' ) }
					/>
				</BaseControl>
			) }

			{ /* Premium Controls */ }
			<BackgroundPremiumControls
				attributes={ attributes }
				setAttributes={ setAttributes }
				activeBreakpoint={ activeBreakpoint }
				controls={ controls }
				currentValues={ currentValues }
				updateAttribute={ updateAttribute }
				resetIndividualProperty={ resetIndividualProperty }
				hasPropertyValue={ hasPropertyValue }
				showIndividualResets={ showIndividualResets }
			/>
		</PanelBody>
	);
};
